import { IDomainConcept } from 'Modules/Studio/constants/Domains/domains.interface';
import { TConceptsMap } from '../ConceptsMap/getDomainConceptsMap';

export type TInheritedDomainPropsAndRels = {
  properties: IDomainConcept['properties'];
  relations: IDomainConcept['relations'];
};

/**
 * Iteratively gets inherited properties and relations for a concept
 * by recursively traversing the inheritedConcepts array.
 * @param {*} inheritedConcepts
 * @param {*} conceptsMap
 * @param {*} base
 * @param {*} memo
 * @returns
 */
export const getInheritedPropsForEditQuestion = (
  inheritedConcepts: string[],
  conceptsMap: TConceptsMap,
  base: TInheritedDomainPropsAndRels = { properties: [], relations: [] },
  memo = new Map()
): TInheritedDomainPropsAndRels => {
  try {
    return inheritedConcepts.reduce((acc: TInheritedDomainPropsAndRels, concept: string) => {
      // Check if concept is already in memo; skip if it is
      // This prevents infinite recursion in case of circular references
      // or repeated concepts in the inheritedConcepts array
      // This is a performance optimization to avoid recalculating the same concept
      // and to prevent infinite loops in case of circular references
      if (memo && memo.has(concept)) {
        return acc;
      }

      memo.set(concept, true);

      const newVal: TInheritedDomainPropsAndRels = {
        ...acc,
        properties: [...acc.properties, ...conceptsMap[concept].properties],
        relations: [...acc.relations, ...conceptsMap[concept].relations],
      };

      if (conceptsMap[concept].inheritedConcepts.length === 0) {
        // base condition, return label, props & relations
        return newVal;
      } else {
        try {
          // recursively pick out inherited props
          return getInheritedPropsForEditQuestion(
            conceptsMap[concept].inheritedConcepts,
            conceptsMap,
            newVal,
            memo
          );
        } catch (e) {
          console.warn('Error while getting inherited properties:', e);
          return newVal;
        }
      }
    }, base);
  } catch (error) {
    console.error(
      'Error in getInheritedProps(); returning empty properties[] & relations[] for inherited props:',
      error
    );
    return base; // Return base if there's an error
  }
};
